// Copyright (c) Docugami, Inc. All rights reserved.

import { Point } from "sdv/Types";
import { Action, ActionId, Axis2dAction, ButtonAction, ButtonState, createAxis2dAction, createButtonAction, createButtonState } from "./Actions";
import { MouseButton } from "./MouseButton";

export interface IInputSource {
    start(controller: InputController): void;
    stop(): void;
    update(): void;
}

export interface IInput {
    getButton(actionId: ActionId): ButtonState;
    getAxis2d(actionId: ActionId): Point | null;
}

/**
 * A snapshot of the input state for a given frame
 */
export class InputState implements IInput {
    private readonly _actions: Map<ActionId, Action> = new Map<ActionId, Action>();
    private readonly _handled: Set<ActionId> = new Set<ActionId>();

    constructor(actions: Map<ActionId, Action>) {
        actions.forEach((action, actionId) => {
            this._actions.set(actionId, { ...action });
        });
    }

    getButton(actionId: ActionId): ButtonState {
        const buttonAction = (this._actions.get(actionId) as ButtonAction);
        const handle = () => this._handled.add(actionId);
        const handled = this._handled.has(actionId);

        if (!buttonAction) {
            return createButtonState(handled, handle);
        }

        return {
            isDown: buttonAction.isDown,
            isUp: buttonAction.isUp,
            isPressed: buttonAction.isPressed,
            handled: handled,
            handle: handle
        };
    }

    getAxis2d(actionId: ActionId): Point | null {
        const action = this._actions.get(actionId) as Axis2dAction;
        return action.valid ? {
            x: action?.x ?? 0,
            y: action?.y ?? 0
        } : null;
    }
}

/**
 * The input controller that receives input from the source and stores the state internally.
 */
export class InputController {
    // Button actions
    private readonly _selectAction = createButtonAction(ActionId.Select);
    private readonly _deselectAction = createButtonAction(ActionId.Deselect);

    // Cursor actions
    private readonly _cursorAction = createAxis2dAction(ActionId.SelectCursor);

    private readonly _actions: Map<ActionId, Action> = new Map<ActionId, Action>([
        [ActionId.Select, this._selectAction],
        [ActionId.SelectCursor, this._cursorAction],
        [ActionId.Deselect, this._deselectAction]
    ]);

    // Mouse action map
    private readonly _mouseActionMap: Map<MouseButton, ActionId> = new Map<MouseButton, ActionId>([
        [MouseButton.Left, ActionId.Select]
    ]);

    // Keyboard action map
    private readonly _keyboardActionMap: Map<string, ActionId> = new Map<string, ActionId>([
        ['Escape', ActionId.Deselect]
    ])

    get input(): IInput {
        const input = new InputState(this._actions);

        // Reset single frame actions
        this._selectAction.isUp = false;
        this._selectAction.isDown = false;
        this._deselectAction.isUp = false;
        this._deselectAction.isDown = false;

        return input;
    }

    onPointerMove(point: Point) {
        this._cursorAction.valid = true;
        this._cursorAction.x = point.x;
        this._cursorAction.y = point.y;
    }

    onPointerLeave() {
        this._cursorAction.x = -1;
        this._cursorAction.y = -1;
        this._cursorAction.valid = false;
    }

    onKeyDown(key: string) {
        const actionId = this._keyboardActionMap.get(key);
        if (!!actionId) {
            const action = this._actions.get(actionId) as ButtonAction;
            action.isDown = true;
            action.isPressed = true;
            action.isUp = false;
        }
    }

    onKeyUp(key: string) {
        const actionId = this._keyboardActionMap.get(key);
        if (!!actionId) {
            const action = this._actions.get(actionId) as ButtonAction;
            action.isDown = false;
            action.isPressed = false;
            action.isUp = true;
        }
    }

    onPointerDown(button: MouseButton) {
        const actionId = this._mouseActionMap.get(button);
        if (!!actionId) {
            const action = this._actions.get(actionId) as ButtonAction;
            action.isDown = true;
            action.isPressed = true;
            action.isUp = false;
        }
    }

    onPointerUp(button: MouseButton) {
        const actionId = this._mouseActionMap.get(button);
        if (!!actionId) {
            const action = this._actions.get(actionId) as ButtonAction;
            action.isDown = false;
            action.isPressed = false;
            action.isUp = true;
        }
    }
}
